import React from "react"
import { useFormik } from "formik"
import moment from "moment"
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { Alert, Col, Form, Row } from "reactstrap"

import {
   AddTrailerOrder,
   GetCustomerListing,
   GetNextAvailableSlot,
   GetProductionLineAvailableSlots,
   GetProductionLinesEstimationListing,
   GeTrailersTypesListing,
   UpdateTimeLineOrder,
} from "api/api.service"
import { AutoComplete } from "components/AutoComplete"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import { Button } from "components/Button"
import FormSkeleton from "components/FormSkeleton/FormSkeleton"
import FormikErrorText from "components/FormikErrorText/FormikErrorText"
import { Modal } from "components/Modal"
import { TimeLineOrderStatus } from "constants"
import SlotTypes from "constants/SlotTypes"
import useAsyncSelect from "hooks/useAsyncSelect"
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest"
import { calculateTrailerStatus } from "pages/Timeline/AllTrailers/AllTrailers"
import { combineSlotOptions, mapSlotsToOptions } from "utils/commonUtils"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import {
   FIELDS_NAME,
   getValidationSchema,
   initialValues,
} from "../../FormConfig"

const INFINITE_TRAILER_TYPE = -1
const INFINITE_CUSTOMER_TYPE = -1
const DATA_KEY = "data"
const TOTAL_KEY = "total"
const WINDOW_DAYS = 30
const NEXT_SLOT_WINDOW_DAYS = 31

const FETCH_PRODUCTION_LINES_ERROR_MESSAGE = "Failed to fetch Production Lines"
const NO_MATCHING_MOULDS_FOUND_ERROR_MESSAGE = "No Matching Moulds Found"
const NO_SLOT_FOUND_ERROR_MESSAGE = "No slot found"
const NO_AVAILABLE_SLOTS_FOUND_ERROR_MESSAGE =
   "Failed to fetch next available slots"

const ORDER_CREATED_SUCCESS_MESSAGE = "Order has been created successfully!"
const ORDER_UPDATED_SUCCESS_MESSAGE = "Order has been updated successfully!"
const ADMIN_WEB = "ADMIN_WEB"

const TITLES = {
   MODAL_ADD_TITLE: "Create Order",
   MODAL_UPDATE_TITLE: "Update Order",
   ADD_BTN_TEXT: "Create",
   UPDATE_BTN_TEXT: "Update",
}
const PRIORITY = [
   {
      value: SlotTypes.REGULAR,
      label: SlotTypes.getDisplayTextKey(SlotTypes.REGULAR),
   },
   {
      value: SlotTypes.PREMIUM,
      label: SlotTypes.getDisplayTextKey(SlotTypes.PREMIUM),
   },
]

const TOTAL_PRODUCTION_LINE_LIMIT = -1

const formatDate = date =>
   convertDateTime({
      date,
      customFormat: dateTimeFormat.americanFormat,
   })

const AddTrailerModal = props => {
   const {
      isDetailView = false,
      isOpen = false,
      currentTrailerItem = null,
      onToggleModal = () => {},
      onSuccess = () => {},
   } = props

   const [isLoading, setIsLoading] = useState(false)
   const [isFetching, setIsFetching] = useState(false)
   const [nextSlotError, setNextSlotError] = useState(null)
   const [fieldStates, setFieldStates] = useState({
      productionLine: {
         isLoading: false,
         isDisabled: false,
      },
      availableSlots: {
         isLoading: false,
         isDisabled: false,
      },
   })
   const [slotsState, setSlotsState] = useState({
      options: [],
      fetching: false,
      hasMore: true,
      startDateCursor: null,
   })
   const abortControllerRef = useRef(null)
   const currentStatus =
      currentTrailerItem?.status == TimeLineOrderStatus.OPEN
         ? calculateTrailerStatus(currentTrailerItem)
         : currentTrailerItem?.status
   const { data } = useLocalPaginatedRequest({
      requestFn: GeTrailersTypesListing,
      params: { limit: INFINITE_TRAILER_TYPE },
      deps: [],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      enabled: !currentTrailerItem,
   })

   const { data: customerData } = useLocalPaginatedRequest({
      requestFn: GetCustomerListing,
      params: { limit: INFINITE_CUSTOMER_TYPE },
      deps: [],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      // enabled: !currentTrailerItem,
   })

   const TRAILER_TYPE_OPTIONS = data?.map(item => ({
      label: item?.trailerType,
      value: item?.id,
   }))

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
         ...initialValues,
         [FIELDS_NAME.PRODUCTION_LINE]: null,
      },
      validationSchema: getValidationSchema(!!currentTrailerItem),
      onSubmit: values => {
         handleSubmit(values)
      },
   })
   const params = {
      trailerId: formik.values[FIELDS_NAME.TRAILER_TYPE]?.value,
      priority: formik.values[FIELDS_NAME.PRIORITY]?.value,
      limit: TOTAL_PRODUCTION_LINE_LIMIT,
      page: 1,
   }

   useEffect(() => {
      if (currentTrailerItem) {
         fetchCurrentTrailerSlots()
      }
   }, [currentTrailerItem])
   const fetchCurrentTrailerSlots = async () => {
      try {
         setIsFetching(true)
         if (currentTrailerItem) {
            formik.setFieldValue(FIELDS_NAME.CUSTOMER_NAME, {
               label: currentTrailerItem?.customer?.name || "",
               value: currentTrailerItem?.customer?.id,
               isNew: Boolean(
                  !(
                     currentTrailerItem?.customer?.id ||
                     currentTrailerItem?.customerId
                  )
               ),
            })

            formik.setFieldValue(FIELDS_NAME.TRAILER_TYPE, {
               id: currentTrailerItem.trailerId,
               label: currentTrailerItem.trailer?.trailerType,
               value: currentTrailerItem.trailerId,
            })

            formik.setFieldValue(FIELDS_NAME.PRIORITY, {
               value: currentTrailerItem.priority,
               label: SlotTypes.getDisplayTextKey(currentTrailerItem?.priority),
            })

            const cells = currentTrailerItem?.timelineCells || []
            if (Array.isArray(cells) && cells.length > 0) {
               const firstCell = cells[0]
               // const lastCell = cells[cells.length - 1]
               const currentSlot = {
                  estimatedStartDate: firstCell?.date,
                  estimatedEndDate: currentTrailerItem?.estimatedDate,
                  slotSequence: firstCell?.slotSequence,
               }
               const preselect = mapSlotsToOptions(
                  [currentSlot],
                  null,
                  formatDate
               )[0]
               formik.setFieldValue(
                  FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                  preselect || null
               )

               setSlotsState({
                  options: [preselect],
                  fetching: false,
                  hasMore: true,
                  startDateCursor: null,
               })
            }
         }
      } finally {
         setIsFetching(false)
      }
   }

   const getProductionLines = async () => {
      if (currentTrailerItem?.id) return
      try {
         setFieldStates(prev => ({
            ...prev,
            productionLine: { ...prev.productionLine, isDisabled: true },
         }))
         const res = await GetProductionLinesEstimationListing(params)
         if (!res?.data?.length) {
            throw new Error(NO_SLOT_FOUND_ERROR_MESSAGE)
         }
         return res
      } catch (error) {
         setNextSlotError(
            error?.message || FETCH_PRODUCTION_LINES_ERROR_MESSAGE
         )
      } finally {
         setFieldStates(prev => ({
            ...prev,
            productionLine: { ...prev.productionLine, isDisabled: false },
         }))
      }
   }
   const isProductionLinesEnabled = useMemo(() => {
      const trailerTypeId = formik.values[FIELDS_NAME.TRAILER_TYPE]?.value
      const priorityValue = formik.values[FIELDS_NAME.PRIORITY]?.value
      return Boolean(trailerTypeId && priorityValue)
   }, [
      formik.values[FIELDS_NAME.TRAILER_TYPE]?.value,
      formik.values[FIELDS_NAME.PRIORITY]?.value,
   ])

   const {
      selectEntity: productionLines,
      handleSearchOnOptions: handleProductionLineSearch,
   } = useAsyncSelect({
      apiFunc: getProductionLines,
      labelKey: "name",
      valueKey: "id",
      dataKey: "data",
      totalKey: "total",
      deps: [
         formik.values[FIELDS_NAME.TRAILER_TYPE],
         formik.values[FIELDS_NAME.PRIORITY],
      ],
      params,
      enabled: isProductionLinesEnabled && !currentTrailerItem,
      onOptionSelect: (option, id) => {
         formik.setFieldValue(FIELDS_NAME.PRODUCTION_LINE, option)
      },
      shouldAppend: false,
   })

   const handleSubmit = values => {
      if (currentTrailerItem) {
         handleUpdate(values)
      } else {
         handleAdd(values)
      }
   }

   const handleUpdate = async values => {
      const payload = {}

      const newCustomerName = values[FIELDS_NAME.CUSTOMER_NAME]

      if (
         newCustomerName &&
         newCustomerName.value !== undefined &&
         newCustomerName.value !== null
      ) {
         if (newCustomerName.isNew) {
            payload.customerName = newCustomerName.label?.trim() || ""
         } else {
            payload.customerId = Number(newCustomerName.value)
         }
      }

      const newPriority = values[FIELDS_NAME.PRIORITY]?.value || null
      if (newPriority && newPriority !== currentTrailerItem?.priority) {
         payload.priority = newPriority
      }

      const cells = currentTrailerItem?.timelineCells || []
      const initialFirstCell =
         Array.isArray(cells) && cells.length > 0 ? cells[0] : null
      const initialStartDate = initialFirstCell?.date || null
      const initialSlotSequence = initialFirstCell?.slotSequence || null

      const selectedSlot = values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]
      if (selectedSlot) {
         const selectedDateStr = moment(selectedSlot.estimatedStartDate).utc().format(
            dateTimeFormat.apiDateFormat
         )
         const initialDateStr = initialStartDate
            ? moment(initialStartDate).utc().format(dateTimeFormat.apiDateFormat)
            : null
         if (
            selectedSlot.slotSequence !== initialSlotSequence ||
            selectedDateStr !== initialDateStr
         ) {
            payload.replacement = {
               slotSequence: selectedSlot.slotSequence,
               date: selectedDateStr,
            }
         }
      }

      if (Object.keys(payload).length === 0) {
         handleModalClose()
         return
      }

      try {
         setIsLoading(true)
         await UpdateTimeLineOrder(currentTrailerItem?.id, payload)
         toast.success(ORDER_UPDATED_SUCCESS_MESSAGE)
         onSuccess()
         handleModalClose()
      } finally {
         setIsLoading(false)
      }
   }

   const handleAdd = async values => {
      let payload = {
         trailerId: values[FIELDS_NAME.TRAILER_TYPE]?.value,
         priority: values[FIELDS_NAME.PRIORITY]?.value || null,
         productionLineId: values[FIELDS_NAME.PRODUCTION_LINE]?.id || null,

         ...(typeof values[FIELDS_NAME.CUSTOMER_NAME]?.value === "string" && {
            customerName: values[FIELDS_NAME.CUSTOMER_NAME]?.value,
         }),
         ...(typeof values[FIELDS_NAME.CUSTOMER_NAME]?.value === "number" && {
            customerId: values[FIELDS_NAME.CUSTOMER_NAME]?.value,
         }),

         slotSequence: values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]?.slotSequence,
         date: moment(
            values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]?.estimatedStartDate
         ).utc().format(dateTimeFormat.apiDateFormat),
         source: ADMIN_WEB,
      }
      try {
         setIsLoading(true)
         await AddTrailerOrder(payload)
         toast.success(ORDER_CREATED_SUCCESS_MESSAGE)
         onSuccess()
         handleModalClose()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }

   const handleModalClose = () => {
      formik.resetForm()
      onToggleModal()
   }

   const resetSlots = ({ clearSelected = true } = {}) => {
      const currentSelection = formik.values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]

      setSlotsState({
         options: clearSelected
            ? []
            : currentSelection
            ? [currentSelection]
            : [],
         fetching: false,
         hasMore: true,
         startDateCursor: null,
      })
      setFieldStates(prev => ({
         ...prev,
         availableSlots: { ...prev.availableSlots, isLoading: false },
      }))
      if (clearSelected) {
         formik.setFieldValue(FIELDS_NAME.NEXT_AVAILABLE_SLOT, null)
      }
   }

   const resolvedProductionLineId = useMemo(() => {
      return (
         formik.values[FIELDS_NAME.PRODUCTION_LINE]?.id ||
         currentTrailerItem?.productionLineId ||
         currentTrailerItem?.productionLine?.id ||
         null
      )
   }, [
      formik.values[FIELDS_NAME.PRODUCTION_LINE]?.id,
      currentTrailerItem?.productionLineId,
      currentTrailerItem?.productionLine?.id,
   ])

   const buildAvailableSlotsParams = startDate => {
      return {
         trailerId: formik.values[FIELDS_NAME.TRAILER_TYPE]?.value,
         priority: formik.values[FIELDS_NAME.PRIORITY]?.value,
         productionLineId: resolvedProductionLineId,
         startDate,
      }
   }
   const fetchAvailableSlots = async (
      initial = false,
      startDateOverride = null
   ) => {
      if (abortControllerRef.current) {
         abortControllerRef.current.abort()
      }

      const newAbortController = new AbortController()
      abortControllerRef.current = newAbortController

      try {
         setNextSlotError(null)
         const trailerTypeId = formik.values[FIELDS_NAME.TRAILER_TYPE]?.value
         const priorityValue = formik.values[FIELDS_NAME.PRIORITY]?.value
         if (!resolvedProductionLineId || !trailerTypeId || !priorityValue)
            return

         const startDateBase = (() => {
            if (startDateOverride) return startDateOverride
            if (initial) {
               // On dependency change (initial load), ignore any previous cursor
               if (
                  formik.values[FIELDS_NAME.PRODUCTION_LINE]?.estimatedStartDate
               )
                  return moment(
                     formik.values[FIELDS_NAME.PRODUCTION_LINE]
                        ?.estimatedStartDate
                  ).utc().format(dateTimeFormat.apiDateFormat)
               return null
            }
            // For pagination, continue from the cursor first
            if (slotsState.startDateCursor) return slotsState.startDateCursor
            if (formik.values[FIELDS_NAME.PRODUCTION_LINE]?.estimatedStartDate)
               return moment(
                  formik.values[FIELDS_NAME.PRODUCTION_LINE]?.estimatedStartDate
               ).utc().format(dateTimeFormat.apiDateFormat)
            return null
         })()

         if (!startDateBase) return

         setSlotsState(prev => ({ ...prev, fetching: true }))
         setFieldStates(prev => ({
            ...prev,
            availableSlots: { ...prev.availableSlots, isLoading: true },
         }))

         const res = await GetProductionLineAvailableSlots(
            buildAvailableSlotsParams(startDateBase),
            newAbortController.signal
         )

         // Check if request was aborted
         if (newAbortController.signal.aborted) return

         // When replacing (initial/startDateOverride), include current selection only for update case
         const shouldReplace = initial || !!startDateOverride
         const currentSelectionForMapping = shouldReplace
            ? currentTrailerItem
               ? formik.values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]
               : null
            : formik.values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]
         const newOptions = mapSlotsToOptions(
            res?.slots || [],
            currentSelectionForMapping,
            formatDate
         )

         setSlotsState(prev => {
            const currentSelection =
               formik.values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]
            const nextOptions = shouldReplace
               ? newOptions
               : combineSlotOptions(prev.options, newOptions, currentSelection)

            return {
               options: nextOptions,
               fetching: false,
               hasMore: (res?.slots || []).length > 0,
               startDateCursor: moment(startDateBase).utc()
                  .add(NEXT_SLOT_WINDOW_DAYS, "days")
                  .format(dateTimeFormat.apiDateFormat),
            }
         })
         setFieldStates(prev => ({
            ...prev,
            availableSlots: { ...prev.availableSlots, isLoading: false },
         }))
      } catch (error) {
         if (error.name === "AbortError") return

         setSlotsState(prev => ({ ...prev, fetching: false }))
         setFieldStates(prev => ({
            ...prev,
            availableSlots: { ...prev.availableSlots, isLoading: false },
         }))
         setNextSlotError(
            error?.message || NO_AVAILABLE_SLOTS_FOUND_ERROR_MESSAGE
         )
         // } finally {
         //    // Only update states if this controller is still the current one
         //    if (abortControllerRef.current === newAbortController) {
         //       setSlotsState(prev => ({ ...prev, fetching: false }))
         //       setFieldStates(prev => ({
         //          ...prev,
         //          availableSlots: { ...prev.availableSlots, isLoading: false },
         //       }))
         //    }
      }
   }

   const handleAvailableSlotsFetch = async () => {
      if (currentTrailerItem && currentStatus !== TimeLineOrderStatus.OPEN)
         return
      const trailerTypeId = formik.values[FIELDS_NAME.TRAILER_TYPE]?.value
      const priorityValue = formik.values[FIELDS_NAME.PRIORITY]?.value
      if (!resolvedProductionLineId || !trailerTypeId || !priorityValue) return

      // Cancel any pending GetProductionLineAvailableSlots calls when GetNextAvailableSlot is called
      if (abortControllerRef.current) {
         abortControllerRef.current.abort()
      }

      // Always clear selection and options when any dependency changes
      resetSlots({ clearSelected: !currentTrailerItem })

      if (currentTrailerItem) {
         try {
            setFieldStates(prev => ({
               ...prev,
               availableSlots: { ...prev.availableSlots, isLoading: true },
            }))
            const res = await GetNextAvailableSlot({
               trailerId: trailerTypeId,
               priority: priorityValue,
               productionLineId: resolvedProductionLineId,
            })

            const startDate = res?.estimatedStartDate
               ? moment(res.estimatedStartDate).utc().format(
                    dateTimeFormat.apiDateFormat
                 )
               : null
            await fetchAvailableSlots(true, startDate)
         } catch (error) {
            setNextSlotError(
               error?.message || NO_AVAILABLE_SLOTS_FOUND_ERROR_MESSAGE
            )
         } finally {
            setFieldStates(prev => ({
               ...prev,
               availableSlots: { ...prev.availableSlots, isLoading: false },
            }))
         }
      } else {
         await fetchAvailableSlots(true)
      }
   }

   useEffect(() => {
      handleAvailableSlotsFetch()
   }, [
      resolvedProductionLineId,
      formik.values[FIELDS_NAME.TRAILER_TYPE]?.value,
      formik.values[FIELDS_NAME.PRIORITY]?.value,
      formik.values[FIELDS_NAME.PRODUCTION_LINE]?.value,
      currentTrailerItem,
   ])

   useEffect(() => {
      return () => {
         if (abortControllerRef.current) {
            abortControllerRef.current.abort()
         }
      }
   }, [])

   const CUSTOMER_NAMES = customerData?.map(item => ({
      label: item.name,
      value: item.id,
   }))

   const productionLineOptions = productionLines?.data
      ? productionLines.data.map(item => ({
           label: item.productionLineName,
           value: item.productionLineName,
           //    uncomment the description line if you want to show the expected timeline
           //   description: `Expected Timeline: ${moment(
           //      item.estimatedStartDate
           //   ).format(dateTimeFormat.americanFormat)} - ${moment(
           //      item.estimatedEndDate
           //   ).format(dateTimeFormat.americanFormat)}`,
           id: item.productionLineId,
           estimatedStartDate: item.estimatedStartDate,
        }))
      : []
   const isButtonDisabled =
      fieldStates.productionLine.isDisabled ||
      !productionLineOptions.length ||
      !formik.values[FIELDS_NAME.PRODUCTION_LINE]?.value ||
      !formik.values[FIELDS_NAME.NEXT_AVAILABLE_SLOT]?.value

   const handleKeyDown = (fieldName, event) => {
      const { key, target } = event

      if (key === "Enter") {
         event.preventDefault()
         const inputValue = target.value?.trim()

         if (inputValue) {
            const newOption = {
               label: inputValue,
               value: inputValue,
               isNew: true,
            }

            if (fieldName === FIELDS_NAME.CUSTOMER_NAME) {
               formik.setFieldValue(fieldName, newOption)
            } else if (fieldName === FIELDS_NAME.TRAILER_TYPE) {
               const existingOption = TRAILER_TYPE_OPTIONS?.find(
                  opt => opt.label.toLowerCase() === inputValue.toLowerCase()
               )
               if (existingOption) {
                  formik.setFieldValue(fieldName, existingOption)
               }
            } else if (fieldName === FIELDS_NAME.PRIORITY) {
               const existingOption = PRIORITY.find(
                  opt => opt.label.toLowerCase() === inputValue.toLowerCase()
               )
               if (existingOption) {
                  formik.setFieldValue(fieldName, existingOption)
               }
            } else if (fieldName === FIELDS_NAME.PRODUCTION_LINE) {
               const existingOption = productionLineOptions?.find(
                  opt => opt.label.toLowerCase() === inputValue.toLowerCase()
               )
               if (existingOption) {
                  formik.setFieldValue(fieldName, existingOption)
               }
            } else if (fieldName === FIELDS_NAME.NEXT_AVAILABLE_SLOT) {
               const existingOption = slotsState.options?.find(
                  opt => opt.label.toLowerCase() === inputValue.toLowerCase()
               )
               if (existingOption) {
                  formik.setFieldValue(fieldName, existingOption)
               }
            }
         }
      } else if (key === "Backspace") {
         if (!target.value || target.value.length === 0) {
            formik.setFieldValue(fieldName, null)
         }
      }
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container justify-content-center mb-0"
         sizeClasses="5"
         backdrop="static"
         modalTitle={
            currentTrailerItem
               ? TITLES.MODAL_UPDATE_TITLE
               : TITLES.MODAL_ADD_TITLE
         }
      >
         {isFetching ? (
            <div>
               <FormSkeleton />
            </div>
         ) : (
            <div style={{ pointerEvents: isLoading ? "none" : "auto" }}>
               <Row>
                  <Col xs={12}>
                     {nextSlotError && (
                        <Alert color="danger" className="mb-3">
                           {nextSlotError}
                        </Alert>
                     )}
                     <Form onSubmit={formik.handleSubmit}>
                        {(!currentTrailerItem ||
                           currentStatus === TimeLineOrderStatus.OPEN) && (
                           <Row className="mt-4 mb-3 align-items-center">
                              <label className="col-md-3 col-form-label clr-theme-primary">
                                 Customer Name
                              </label>
                              <div className="col-md-9">
                                 <AutoComplete
                                    name={FIELDS_NAME.CUSTOMER_NAME}
                                    placeholder="Select Customer Name"
                                    classNamePrefix="add-new-req"
                                    customStyles={autoCompleteStyles.STYLE_FOUR}
                                    options={CUSTOMER_NAMES}
                                    value={
                                       formik.values[FIELDS_NAME.CUSTOMER_NAME]
                                    }
                                    onInputChange={(inputValue, { action }) => {
                                       if (action === "input-change") {
                                          formik.setFieldValue(
                                             FIELDS_NAME.CUSTOMER_NAME,
                                             {
                                                label: inputValue,
                                                value: inputValue,
                                                isNew: true,
                                             }
                                          )
                                       }
                                    }}
                                    onChange={option => {
                                       if (!option) return

                                       if (
                                          CUSTOMER_NAMES.some(
                                             c => c.value === option.value
                                          )
                                       ) {
                                          formik.setFieldValue(
                                             FIELDS_NAME.CUSTOMER_NAME,
                                             {
                                                label: option.label,
                                                value: option.value,
                                                isNew: false,
                                             }
                                          )
                                       } else {
                                          formik.setFieldValue(
                                             FIELDS_NAME.CUSTOMER_NAME,
                                             {
                                                label:
                                                   option.label || option.value,
                                                value: option.value,
                                                isNew: true,
                                             }
                                          )
                                       }
                                    }}
                                    onKeyDown={event =>
                                       handleKeyDown(
                                          FIELDS_NAME.CUSTOMER_NAME,
                                          event
                                       )
                                    }
                                    onBlur={() =>
                                       formik.setFieldTouched(
                                          FIELDS_NAME.CUSTOMER_NAME,
                                          true
                                       )
                                    }
                                 />

                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.CUSTOMER_NAME}
                                 />
                              </div>
                           </Row>
                        )}
                        {!currentTrailerItem && (
                           <Row className="mt-4 mb-3 align-items-center">
                              <label className="col-md-3 col-form-label clr-theme-primary">
                                 Trailer Type
                              </label>
                              <div className="col-md-9">
                                 <AutoComplete
                                    name={FIELDS_NAME.TRAILER_TYPE}
                                    placeholder="Select Trailer Type"
                                    classNamePrefix="add-new-req"
                                    customStyles={autoCompleteStyles.STYLE_FOUR}
                                    options={TRAILER_TYPE_OPTIONS}
                                    value={
                                       formik.values[FIELDS_NAME.TRAILER_TYPE]
                                    }
                                    onChange={option => {
                                       if (
                                          option.value ==
                                          formik.values[
                                             FIELDS_NAME.TRAILER_TYPE
                                          ]?.value
                                       ) {
                                          return
                                       }
                                       formik.setFieldValue(
                                          FIELDS_NAME.TRAILER_TYPE,
                                          option || ""
                                       )
                                       formik.setFieldValue(
                                          FIELDS_NAME.PRODUCTION_LINE,
                                          null
                                       )
                                       formik.setFieldValue(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                                          null
                                       )
                                    }}
                                    onBlur={() =>
                                       formik.setFieldTouched(
                                          FIELDS_NAME.TRAILER_TYPE,
                                          true
                                       )
                                    }
                                 />
                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.TRAILER_TYPE}
                                 />
                              </div>
                           </Row>
                        )}
                        {(!currentTrailerItem ||
                           currentStatus === TimeLineOrderStatus.OPEN) && (
                           <Row className="mt-4 mb-3 align-items-center">
                              <label className="col-md-3 col-form-label clr-theme-primary">
                                 Priority
                              </label>
                              <div className="col-md-9">
                                 <AutoComplete
                                    name={FIELDS_NAME.PRIORITY}
                                    placeholder="Select priority"
                                    classNamePrefix="add-new-req"
                                    options={PRIORITY}
                                    customStyles={autoCompleteStyles.STYLE_FOUR}
                                    value={formik.values[FIELDS_NAME.PRIORITY]}
                                    onChange={option => {
                                       if (
                                          option.value ==
                                          formik.values[FIELDS_NAME.PRIORITY]
                                             ?.value
                                       ) {
                                          return
                                       }
                                       formik.setFieldValue(
                                          FIELDS_NAME.PRIORITY,
                                          option || ""
                                       )
                                       formik.setFieldValue(
                                          FIELDS_NAME.PRODUCTION_LINE,
                                          null
                                       )
                                       formik.setFieldValue(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                                          null
                                       )
                                    }}
                                    onBlur={() =>
                                       formik.setFieldTouched(
                                          FIELDS_NAME.PRIORITY,
                                          true
                                       )
                                    }
                                 />
                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.PRIORITY}
                                 />
                              </div>
                           </Row>
                        )}

                        {!currentTrailerItem && (
                           <Row className="mt-4 mb-3 align-items-center">
                              <label className="col-md-3 col-form-label clr-theme-primary">
                                 Production Line
                              </label>
                              <div className="col-md-9">
                                 <AutoComplete
                                    name={FIELDS_NAME.PRODUCTION_LINE}
                                    placeholder="Select Production Line"
                                    classNamePrefix="add-new-req"
                                    options={productionLineOptions}
                                    customStyles={autoCompleteStyles.STYLE_FOUR}
                                    value={
                                       formik.values[
                                          FIELDS_NAME.PRODUCTION_LINE
                                       ]
                                    }
                                    onChange={async option => {
                                       formik.setFieldError(
                                          FIELDS_NAME.PRODUCTION_LINE,
                                          undefined
                                       )

                                       await formik.setFieldValue(
                                          FIELDS_NAME.PRODUCTION_LINE,
                                          option || null
                                       )

                                       await formik.validateField(
                                          FIELDS_NAME.PRODUCTION_LINE
                                       )

                                       formik.setFieldValue(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                                          null
                                       )
                                    }}
                                    onInputChange={handleProductionLineSearch}
                                    onBlur={() =>
                                       formik.setFieldTouched(
                                          FIELDS_NAME.PRODUCTION_LINE,
                                          true
                                       )
                                    }
                                    isDisabled={
                                       fieldStates.productionLine.isDisabled ||
                                       !productionLineOptions.length ||
                                       productionLines.fetching
                                    }
                                    isLoading={
                                       fieldStates.productionLine.isDisabled ||
                                       productionLines.fetching
                                    }
                                 />
                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.PRODUCTION_LINE}
                                 />
                              </div>
                           </Row>
                        )}

                        {(!currentTrailerItem ||
                           currentStatus === TimeLineOrderStatus.OPEN) && (
                           <Row className="mt-2 mb-3 align-items-center">
                              <label className="col-md-3 col-form-label clr-theme-primary">
                                 Available Slots
                              </label>
                              <div className="col-md-9">
                                 <AutoComplete
                                    name={FIELDS_NAME.NEXT_AVAILABLE_SLOT}
                                    placeholder="Select Available Slot"
                                    classNamePrefix="add-new-req"
                                    options={slotsState.options}
                                    customStyles={
                                       autoCompleteStyles.STYLE_EIGHT
                                    }
                                    value={
                                       formik.values[
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT
                                       ]
                                    }
                                    onChange={async option => {
                                       await formik.setFieldValue(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                                          option || null
                                       )
                                       await formik.validateField(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT
                                       )
                                    }}
                                    handleScroll={() => {
                                       if (
                                          slotsState.hasMore &&
                                          !slotsState.fetching
                                       ) {
                                          fetchAvailableSlots(false)
                                       }
                                    }}
                                    onBlur={() =>
                                       formik.setFieldTouched(
                                          FIELDS_NAME.NEXT_AVAILABLE_SLOT,
                                          true
                                       )
                                    }
                                    isDisabled={
                                       fieldStates.availableSlots.isDisabled ||
                                       !resolvedProductionLineId
                                    }
                                    isLoading={
                                       fieldStates.availableSlots.isDisabled ||
                                       fieldStates.availableSlots.isLoading ||
                                       slotsState.fetching
                                    }
                                 />
                                 <FormikErrorText
                                    formikInstance={formik}
                                    fieldName={FIELDS_NAME.NEXT_AVAILABLE_SLOT}
                                 />
                              </div>
                           </Row>
                        )}

                        <div className="d-flex justify-content-center mt-3 mt-2">
                           <Button
                              isLoading={isLoading}
                              disabled={
                                 !currentTrailerItem ? isButtonDisabled : false
                              }
                              type={"submit"}
                              title={
                                 currentTrailerItem
                                    ? TITLES.UPDATE_BTN_TEXT
                                    : TITLES.ADD_BTN_TEXT
                              }
                              className="header-button"
                           />
                        </div>
                     </Form>
                  </Col>
               </Row>
            </div>
         )}
      </Modal>
   )
}

export default AddTrailerModal
